export interface ReturnCustomizationSummary {
  version: 1;
  policyRevision: number;
  kind: 'HEIGHT';
  heightValueId: string;
  heightLabel: string;
  heightValueLabel: string;
}
export interface ReturnCustomizationAcknowledgment {
  id: string;
  revision: number;
  actorId: string;
  acknowledgedAt: string;
  requestId: string;
  intentDigest: string;
}
export interface ReturnCustomizationReview {
  version: 1;
  storeId: string;
  returnId: string;
  status:
    | 'NOT_REQUIRED'
    | 'PREPARING_INSTRUCTIONS'
    | 'AWAITING_ACKNOWLEDGMENT'
    | 'ACKNOWLEDGED'
    | 'PROCESSING'
    | 'READY'
    | 'REVIEW_REQUIRED'
    | 'UNAVAILABLE';
  intentId: string | null;
  revision: number | null;
  intentDigest: string | null;
  replacementVariantId: string | null;
  quantity: number | null;
  selectedOptions: Array<{ name: string; value: string }>;
  replacementCustomization: ReturnCustomizationSummary | null;
  acknowledgment: ReturnCustomizationAcknowledgment | null;
  canAcknowledge: boolean;
  instructionLocation: 'ORIGINAL_ORDER_METAFIELD' | null;
  fulfillmentMode: 'MANUAL_TEAM_ACK' | null;
}
const uuid = (v: unknown): v is string =>
  typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v,
  );
const digest = (v: unknown): v is string =>
  typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
const revision = (v: unknown): v is number =>
  Number.isSafeInteger(v) && Number(v) > 0 && Number(v) <= 2147483647;
const text = (v: unknown, max = 120): v is string =>
  typeof v === 'string' &&
  v === v.trim() &&
  v.length > 0 &&
  v.length <= max &&
  !/[\p{Cc}\p{Cf}]/u.test(v);
const identity = (v: unknown): v is string =>
  typeof v === 'string' && /^[!-~]{1,255}$/.test(v);
function requireValue(v: unknown): asserts v {
  if (!v) throw new Error('Replacement instructions could not be verified.');
}
function record(v: unknown, keys: string[]): Record<string, any> {
  requireValue(v && typeof v === 'object' && !Array.isArray(v));
  const prototype = Object.getPrototypeOf(v);
  requireValue(prototype === Object.prototype || prototype === null);
  const d = Object.getOwnPropertyDescriptors(v);
  requireValue(
    Reflect.ownKeys(d).length === keys.length &&
      keys.every((k) => Object.hasOwnProperty.call(d, k)) &&
      Object.values(d).every((field) => 'value' in field && field.enumerable),
  );
  return Object.fromEntries(
    keys.map((key) => [key, d[key].value]),
  );
}
function options(v: unknown): Array<{ name: string; value: string }> {
  requireValue(Array.isArray(v) && v.length <= 3);
  const d = Object.getOwnPropertyDescriptors(v);
  requireValue(Reflect.ownKeys(d).length === v.length + 1);
  const result = Array.from({ length: v.length }, (_, i) => {
    const field = d[String(i)];
    requireValue(field && 'value' in field);
    const o = record(field.value, ['name', 'value']);
    requireValue(text(o.name, 120) && text(o.value, 120));
    return { name: o.name as string, value: o.value as string };
  });
  requireValue(new Set(result.map((o) => o.name)).size === result.length);
  return result;
}
export function customizationInstructionKey(
  value: ReturnCustomizationReview,
): string {
  return JSON.stringify({
    intentId: value.intentId,
    intentDigest: value.intentDigest,
    replacementVariantId: value.replacementVariantId,
    quantity: value.quantity,
    selectedOptions: value.selectedOptions,
    replacementCustomization: value.replacementCustomization,
    instructionLocation: value.instructionLocation,
    fulfillmentMode: value.fulfillmentMode,
  });
}
/** The JSON scalar is closed and manager-scoped; malformed reads never imply acknowledgment. */
export function parseReturnCustomizationReview(
  value: unknown,
  storeId: string,
  returnId: string,
): ReturnCustomizationReview | null {
  try {
    const r = record(value, [
      'version',
      'storeId',
      'returnId',
      'status',
      'intentId',
      'revision',
      'intentDigest',
      'replacementVariantId',
      'quantity',
      'selectedOptions',
      'replacementCustomization',
      'acknowledgment',
      'canAcknowledge',
      'instructionLocation',
      'fulfillmentMode',
    ]);
    requireValue(
      r.version === 1 &&
        uuid(storeId) &&
        uuid(returnId) &&
        r.storeId === storeId &&
        r.returnId === returnId &&
        [
          'NOT_REQUIRED',
          'PREPARING_INSTRUCTIONS',
          'AWAITING_ACKNOWLEDGMENT',
          'ACKNOWLEDGED',
          'PROCESSING',
          'READY',
          'REVIEW_REQUIRED',
          'UNAVAILABLE',
        ].includes(r.status) &&
        typeof r.canAcknowledge === 'boolean',
    );
    const selectedOptions = options(r.selectedOptions),
      empty =
        [
          'intentId',
          'revision',
          'intentDigest',
          'replacementVariantId',
          'quantity',
          'replacementCustomization',
          'acknowledgment',
          'instructionLocation',
          'fulfillmentMode',
        ].every((k) => r[k] === null) &&
        selectedOptions.length === 0 &&
        !r.canAcknowledge;
    if (['NOT_REQUIRED', 'UNAVAILABLE'].includes(r.status)) {
      requireValue(empty);
      return { ...r, selectedOptions } as ReturnCustomizationReview;
    }
    if (r.status === 'REVIEW_REQUIRED' && empty)
      return { ...r, selectedOptions } as ReturnCustomizationReview;
    requireValue(
      uuid(r.intentId) &&
        revision(r.revision) &&
        digest(r.intentDigest) &&
        typeof r.replacementVariantId === 'string' &&
        /^gid:\/\/shopify\/ProductVariant\/[1-9]\d{0,140}$/.test(
          r.replacementVariantId,
        ) &&
        Number.isSafeInteger(r.quantity) &&
        r.quantity > 0 &&
        r.quantity <= 100 &&
        selectedOptions.length > 0 &&
        r.instructionLocation === 'ORIGINAL_ORDER_METAFIELD' &&
        r.fulfillmentMode === 'MANUAL_TEAM_ACK',
    );
    const summary = record(r.replacementCustomization, [
      'version',
      'policyRevision',
      'kind',
      'heightValueId',
      'heightLabel',
      'heightValueLabel',
    ]);
    requireValue(
      summary.version === 1 &&
        revision(summary.policyRevision) &&
        summary.kind === 'HEIGHT' &&
        typeof summary.heightValueId === 'string' &&
        /^[a-z0-9][a-z0-9_-]{0,63}$/.test(summary.heightValueId) &&
        text(summary.heightLabel) &&
        text(summary.heightValueLabel),
    );
    let acknowledgment: ReturnCustomizationAcknowledgment | null = null;
    if (r.acknowledgment !== null) {
      const ack = record(r.acknowledgment, [
        'id',
        'revision',
        'actorId',
        'acknowledgedAt',
        'requestId',
        'intentDigest',
      ]);
      requireValue(
        uuid(ack.id) &&
          revision(ack.revision) &&
          ack.revision <= r.revision &&
          uuid(ack.actorId) &&
          typeof ack.acknowledgedAt === 'string' &&
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(
            ack.acknowledgedAt,
          ) &&
          Number.isFinite(Date.parse(ack.acknowledgedAt)) &&
          identity(ack.requestId) &&
          ack.intentDigest === r.intentDigest,
      );
      acknowledgment = ack as ReturnCustomizationAcknowledgment;
    }
    requireValue(
      !r.canAcknowledge ||
        (r.status === 'AWAITING_ACKNOWLEDGMENT' && acknowledgment === null),
    );
    requireValue(
      r.status !== 'PREPARING_INSTRUCTIONS' ||
        (!r.canAcknowledge && acknowledgment === null),
    );
    requireValue(
      !['ACKNOWLEDGED', 'PROCESSING', 'READY'].includes(r.status) ||
        acknowledgment !== null,
    );
    requireValue(
      r.status !== 'AWAITING_ACKNOWLEDGMENT' || acknowledgment === null,
    );
    return {
      ...r,
      selectedOptions,
      replacementCustomization: summary,
      acknowledgment,
    } as ReturnCustomizationReview;
  } catch {
    return null;
  }
}
